/**
 * Hook for resolving a set of association targets to full CrmObjects.
 *
 * Store-first: reads from the centralized CRM object store via
 * `useStoreObjects` and only fires a network fetch (through `ensureEntity`,
 * the 50ms batcher) when a target isn't already hydrated. If the slideout
 * or a previous render has already cached the target, rendering happens
 * synchronously off the store with no API call.
 *
 * WebSocket updates keep returned objects fresh automatically — the store
 * is a live subscription. This is the fix for the previous React-Query-
 * only implementation, which would re-fetch targets that the slideout had
 * already placed in the store, and would return stale data after a WS
 * event because the RQ cache wasn't reactive to store mutations.
 *
 * ## Errors
 *
 * `error` and `hasPartialError` are derived from the batcher's failure
 * registry (see `entityFetchBatcher.subscribeToFetchFailures`). When any
 * target's most recent fetch attempt failed, `hasPartialError` is true;
 * `error` surfaces a single representative Error for callers that only
 * render a top-level error message. Calling `refetch` (or re-rendering
 * with the same association list) re-runs `ensureEntity`, which clears
 * the failure flag for those IDs so the error UI dismisses on retry.
 */

import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import {
  ensureEntity,
  getFailureVersion,
  hasFailedFetch,
  subscribeToFetchFailures,
} from '@/lib/crm/object-cards/entityFetchBatcher';
import { mergeProjection, useActivePreviewHandle } from '@/lib/crm/preview';
import { useStoreObjectsMap } from '@/lib/crm/store';

import type { CrmObject } from '../../../types/crm';

export interface Association {
  association_type_name: string;
  target_entity_id: string;
}

export interface AssociationWithObject extends Association {
  /** Resolved CRM object from the store, or null while the fetch is in flight. */
  object: CrmObject | null;
  /** True when the target isn't yet in the store (fetch pending). */
  loading: boolean;
}

/**
 * Source entity types whose associations we don't want to eagerly hydrate.
 * Meetings can have thousands of association targets (every attendee is a
 * user with their own dense association graph); trying to ensure each one
 * triggers a prohibitively expensive batch fetch.
 *
 * When `sourceEntityTypeName` matches one of these, we skip `ensureEntity`
 * and return the existing store contents (usually nothing) so the caller
 * renders fallback UI instead of waiting forever.
 */
const SKIP_ENSURE_FOR_SOURCE_TYPES = new Set(['ahoy_meeting']);

export interface UseAssociationsOptions {
  /**
   * When `false`, callers opt out of eager hydration entirely: the hook
   * only reads what the store already has and does NOT trigger a batched
   * fetch for missing targets. Defaults to `true`.
   *
   * Callers pass `false` when hydrating would be prohibitively expensive —
   * e.g. meeting views whose attendee graphs can include thousands of
   * users, each with their own dense association set. In those cases the
   * caller typically accepts "data may be missing if not already in the
   * store" rather than fan out a large batch fetch on every render.
   */
  includeAssociations?: boolean;
  /**
   * Entity type of the source object whose associations are being resolved.
   * Used to automatically suppress eager hydration for known-heavy types
   * (see SKIP_ENSURE_FOR_SOURCE_TYPES).
   */
  sourceEntityTypeName?: string;
}

/**
 * Resolve a set of associations to their target CrmObjects, reading from
 * the store and triggering a batched fetch only for missing entries.
 */
export function useAssociations(
  teamId: number | undefined,
  associations: Association[] = [],
  options?: UseAssociationsOptions
) {
  // Suppress hydration when either:
  //   1. The caller explicitly opts out via `includeAssociations: false`
  //      (meeting views etc. that accept missing data rather than fan out
  //      a batch fetch across thousands of attendees), OR
  //   2. The source entity type is known-heavy (see SKIP_ENSURE_FOR_SOURCE_TYPES).
  const includeAssociations = options?.includeAssociations ?? true;
  const shouldEnsure =
    includeAssociations &&
    !(
      options?.sourceEntityTypeName &&
      SKIP_ENSURE_FOR_SOURCE_TYPES.has(options.sourceEntityTypeName)
    );

  // Stable list of target IDs. Sorted so membership changes trigger a new
  // reference but pure reordering of the caller's list doesn't.
  const targetIds = useMemo(() => {
    const ids = associations.map(a => a.target_entity_id).filter(Boolean);
    return ids.slice().sort();
  }, [associations]);

  // Preview projection. An association target can be an entity that only
  // exists in a proposal preview (a sibling "create company" the proposal
  // will make). The server has never seen that id, so the store can never
  // hold it — without this the target resolves to null and the caller drops
  // the row (see overview-section's "unresolved association, skip
  // defensively"). Context inside a provider subtree, else the ACTIVE
  // registered provider, so this also resolves inside the out-of-subtree
  // record slideout. Null when no preview is mounted → everything below is
  // a passthrough.
  const preview = useActivePreviewHandle();

  const overlaySubscribe = useCallback(
    (cb: () => void) => {
      if (!preview) {
        return () => {};
      }
      const unsubs = targetIds.map(id => preview.subscribeToOverlay(id, cb));
      return () => {
        for (const unsub of unsubs) {
          unsub();
        }
      };
    },
    [preview, targetIds]
  );
  // Value-compared snapshot: strings compare by value, so an unchanged set of
  // overlays yields an equal string and `useSyncExternalStore` sees no change
  // (same JSON-diff idiom `createPreviewStore` uses to decide notifications).
  const overlayGetSnapshot = useCallback(
    () =>
      preview
        ? JSON.stringify(targetIds.map(id => preview.getOverlay(id) ?? null))
        : '',
    [preview, targetIds]
  );
  const overlayVersion = useSyncExternalStore(
    overlaySubscribe,
    overlayGetSnapshot,
    overlayGetSnapshot
  );

  // A `create` overlay's id has no record on the server — fetching it would
  // 404 and mark the target failed, which surfaces as "associations failed to
  // load" over a target that resolved perfectly well from the projection.
  const shouldFetchTarget = useCallback(
    (id: string) => preview?.getOverlay(id)?.kind !== 'create',
    [preview]
  );

  // Trigger a batched fetch for any targets not already complete in the
  // store. `ensureEntity` is idempotent — no-op when the store has a
  // complete entry or the same ID is already in flight.
  useEffect(() => {
    if (!shouldEnsure || !teamId || teamId <= 0) {
      return;
    }
    for (const id of targetIds) {
      if (!shouldFetchTarget(id)) {
        continue;
      }
      ensureEntity(teamId, id);
    }
  }, [teamId, targetIds, shouldEnsure, shouldFetchTarget]);

  // Live store read — re-renders when any watched target arrives / updates.
  //
  // Keyed by the REQUESTED target id (not the canonical `entity_id` on the
  // stored CrmObject). When a target has been merged, the server response
  // maps the pre-merge request id to a post-merge object whose `entity_id`
  // differs — `useStoreObjectsMap` resolves the alias internally so the
  // caller-facing map key matches the id the caller passed in.
  const storeObjectsById = useStoreObjectsMap(targetIds);

  // Overlay the preview projection on top of the store map. A `create`
  // target projects from nothing (the record does not exist yet); an
  // `update` target merges overlay-wins values over its live store entry.
  const resolvedObjectsById = useMemo(() => {
    if (!preview) {
      return storeObjectsById;
    }
    let merged: Map<string, CrmObject> | null = null;
    for (const id of targetIds) {
      const overlay = preview.getOverlay(id);
      if (!overlay) {
        continue;
      }
      const projected = mergeProjection(
        storeObjectsById.get(id),
        overlay,
        teamId ?? 0
      );
      if (projected) {
        merged ??= new Map(storeObjectsById);
        merged.set(id, projected);
      }
    }
    return merged ?? storeObjectsById;
    // `overlayVersion` bumps this recompute when an overlay's content changes
    // (saved edit, selection toggle) without the store map changing identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- overlayVersion bumps recompute
  }, [preview, storeObjectsById, targetIds, teamId, overlayVersion]);

  // Subscribe to the batcher's failure notifications so the hook re-renders
  // when a target fails (or a retry clears the flag). The snapshot is an
  // incrementing counter — using failedEntityIds directly would either give
  // an unstable reference or force us to serialize the set here.
  const failureVersion = useSyncExternalStore(
    subscribeToFetchFailures,
    getFailureVersion,
    getFailureVersion
  );

  const resolvedAssociations = useMemo<AssociationWithObject[]>(
    () =>
      associations.map(assoc => {
        // Guard against empty/falsy target_entity_id: the lookup below would
        // always miss for "" but empty IDs are excluded from targetIds so no
        // fetch fires, producing an infinite loading state. Return
        // loading:false immediately.
        if (!assoc.target_entity_id) {
          return { ...assoc, object: null, loading: false };
        }
        const object = resolvedObjectsById.get(assoc.target_entity_id) ?? null;
        // An unresolved target is "loading" only when:
        //   - we intend to fetch it (shouldEnsure) — otherwise callers
        //     that opt out (meetings) would sit in a forever-loading state
        //     with no fetch to complete
        //   - its last attempt hasn't failed — a failed fetch is a
        //     terminal state; callers should surface the error UI
        //     instead of an infinite skeleton
        const failed = hasFailedFetch(assoc.target_entity_id);
        return {
          ...assoc,
          object,
          loading: shouldEnsure && object === null && !failed,
        };
      }),
    // `failureVersion` participates in the memo so a failure toggle
    // re-runs this calculation even though the store list is unchanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- failureVersion bumps recompute
    [associations, resolvedObjectsById, shouldEnsure, failureVersion]
  );

  const isLoading = resolvedAssociations.some(a => a.loading);

  const hasPartialError = useMemo(
    () =>
      resolvedAssociations.some(
        a => a.object === null && hasFailedFetch(a.target_entity_id)
      ),
    // `failureVersion` is captured via useSyncExternalStore; keeping it
    // in the dep list forces a recompute when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- failureVersion bumps recompute
    [resolvedAssociations, failureVersion]
  );

  // When EVERY target failed, surface a top-level error for callers that
  // render an error state (e.g. overview-section's fatal path). Partial
  // failures stay on `hasPartialError` only.
  const error = useMemo<Error | null>(() => {
    if (resolvedAssociations.length === 0) {
      return null;
    }
    const allFailed = resolvedAssociations.every(
      a => a.object === null && hasFailedFetch(a.target_entity_id)
    );
    return allFailed ? new Error("We couldn't load associations.") : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- failureVersion bumps recompute
  }, [resolvedAssociations, failureVersion]);

  // `refetch` re-queues each missing target through the batcher. The
  // failure-aware `ensureEntity` clears the failed flag when it runs,
  // so error UI dismisses on retry even before the request returns.
  const refetch = useCallback(() => {
    if (!teamId || teamId <= 0) {
      return Promise.resolve();
    }
    for (const id of targetIds) {
      if (!shouldFetchTarget(id)) {
        continue;
      }
      ensureEntity(teamId, id);
    }
    return Promise.resolve();
  }, [teamId, targetIds, shouldFetchTarget]);

  return {
    associations: resolvedAssociations,
    isLoading,
    error,
    hasPartialError,
    refetch,
  };
}
